import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListQualificationFrameworksResult,
  type RecordQualificationResult,
} from '@whinerp/client';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';

/**
 * How far a record has been qualified (G-053).
 *
 * The headline is a sentence, not a percentage. "Two of four asked, two still
 * unexamined" is a state somebody can act on; "50%" is a number that gets
 * pasted into a forecast, and a lead with two unknowns is not fifty per cent
 * anything.
 *
 * Answering asks for the evidence in the same step as the answer, because a
 * criterion ticked without it is a rep's optimism with a label on it - and a
 * pipeline review reads the tick as though somebody had asked.
 */

type Answer = RecordQualificationResult['qualification']['answers'][number];
const STATES = ['met', 'not-met', 'refused'] as const;

export function QualificationPanel({
  objectKey,
  recordId,
}: {
  objectKey: string;
  recordId: string;
}): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const [answering, setAnswering] = useState<string | null>(null);
  const [evidence, setEvidence] = useState('');

  const frameworks = useQuery<ListQualificationFrameworksResult, ApiError>({
    queryKey: ['qualification-frameworks', objectKey],
    queryFn: () => api.listQualificationFrameworks({ query: { objectKey: objectKey as 'lead' } }),
    enabled: objectKey === 'lead' || objectKey === 'deal',
  });

  const frameworkId = frameworks.data?.frameworks.find((framework) => framework.active)?.frameworkId ?? null;

  const state = useQuery<RecordQualificationResult, ApiError>({
    queryKey: ['qualification', objectKey, recordId, frameworkId],
    queryFn: () =>
      api.recordQualification({ path: { objectKey, recordId }, query: { frameworkId: frameworkId ?? '' } }),
    enabled: frameworkId !== null,
  });

  const answer = useMutation({
    mutationFn: (input: { criterion: string; state: (typeof STATES)[number] }) =>
      api.answerQualificationCriterion({
        path: { objectKey, recordId },
        body: {
          frameworkId: frameworkId ?? '',
          criterion: input.criterion,
          state: input.state,
          evidence: evidence.trim() || null,
        },
      }),
    onSuccess: async () => {
      setAnswering(null);
      setEvidence('');
      await queryClient.invalidateQueries({ queryKey: ['qualification', objectKey, recordId] });
    },
  });

  // Nothing to show on an object nobody qualifies, and nothing to show before
  // a workspace has chosen a framework: an empty panel on every record is a
  // panel people learn to scroll past.
  if (objectKey !== 'lead' && objectKey !== 'deal') return null;
  if (frameworks.isPending) return <Skeleton className="h-block-md w-full" />;
  if (frameworkId === null) return null;

  const qualification = state.data?.qualification;

  return (
    <Panel data-region="qualification">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <SectionLabel>{qualification?.frameworkName ?? t('qualification.title')}</SectionLabel>
          {/* The sentence, in place of a score. */}
          {qualification ? (
            <span data-qualification-summary className="text-meta text-ink-meta">
              {qualification.summary}
            </span>
          ) : null}
        </div>

        {state.isPending ? <Skeleton className="h-block-md w-full" /> : null}

        <ul className="flex flex-col gap-1">
          {(qualification?.answers ?? []).map((row: Answer) => (
            <li
              key={row.criterion}
              data-criterion={row.criterion}
              data-criterion-state={row.state}
              className="flex flex-col gap-1 border-t border-t-rule pt-1 first:border-t-0 first:pt-0"
            >
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-dense text-ink">{row.label}</span>
                <span
                  className={
                    row.state === 'met'
                      ? 'rounded-chip bg-success-tint px-1.5 text-meta text-success-text'
                      : row.state === 'not-met'
                        ? 'rounded-chip bg-danger-tint px-1.5 text-meta text-danger-text'
                        : row.state === 'refused'
                          ? 'rounded-chip bg-warning-tint px-1.5 text-meta text-warning-text'
                          : 'rounded-chip bg-sunken px-1.5 text-meta text-ink-600'
                  }
                >
                  {t(`qualification.state.${row.state}` as 'qualification.state.met')}
                </span>
                <Button
                  intent="ghost"
                  size="small"
                  className="ms-auto"
                  data-criterion-answer={row.criterion}
                  onClick={() => {
                    setAnswering(row.criterion);
                    setEvidence(row.evidence ?? '');
                  }}
                >
                  {t('qualification.answer')}
                </Button>
              </div>

              {/* What it is based on, shown with the answer: the evidence is
                  the work, the state is only a summary of it. */}
              {row.evidence ? (
                <span data-criterion-evidence={row.criterion} className="text-meta text-ink-meta">
                  {row.evidence}
                  {row.answeredByName ? ` — ${row.answeredByName}` : ''}
                </span>
              ) : null}

              {answering === row.criterion ? (
                <div className="flex flex-col gap-1">
                  <textarea
                    data-criterion-evidence-input
                    aria-label={t('qualification.evidence')}
                    placeholder={t('qualification.evidence')}
                    rows={2}
                    value={evidence}
                    onChange={(event) => setEvidence(event.target.value)}
                    className="rounded-control border border-control bg-surface px-2 py-1 text-body text-ink"
                  />
                  <div className="flex flex-wrap gap-2">
                    {STATES.map((value) => (
                      <Button
                        key={value}
                        intent={value === 'met' ? 'primary' : 'secondary'}
                        size="small"
                        data-criterion-state-button={value}
                        // "They would not say" needs no evidence: the refusal
                        // is the evidence.
                        disabled={answer.isPending || (value !== 'refused' && evidence.trim() === '')}
                        onClick={() => answer.mutate({ criterion: row.criterion, state: value })}
                      >
                        {t(`qualification.state.${value}` as 'qualification.state.met')}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {answer.error ? (
          <p data-qualification-refusal className="text-meta text-danger-text">{errorText(answer.error)}</p>
        ) : null}
      </div>
    </Panel>
  );
}
